/**
 * The Engine: owns the kernel (clock, change scheduler, log) and boots modules.
 *
 * Boot is observable. Every module emits `module:start` / `module:done` /
 * `module:error` events and writes to the Log, and the boot screen renders
 * exactly those. Nothing on the boot screen is scripted (CLAUDE.md rule 7).
 *
 * Failure policy (architecture §2.2): a required module that throws fails the
 * boot; an `optional` module that throws is logged as a warning and skipped,
 * along with anything that depends on it.
 */
import { ChangeScheduler } from "./ChangeScheduler";
import type { Clock } from "./Clock";
import { Emitter } from "./Emitter";
import { Log } from "./Log";
import { sortModules } from "./module";
import type { AnyEngineModule, EngineKernel, OptionsOf, ServicesOf } from "./module";

export interface EngineOptions {
  clock: Clock;
  /** Shown in the first boot line: "kn.engine <version> · editor build". */
  version: string;
  logCapacity?: number;
}

export interface EngineEvents {
  "boot:start": { readonly modules: readonly string[] };
  "module:start": { readonly name: string };
  "module:done": { readonly name: string; readonly ms: number };
  "module:error": { readonly name: string; readonly error: Error; readonly optional: boolean };
  "boot:done": { readonly ms: number; readonly modules: readonly string[] };
}

export class Engine {
  readonly kernel: EngineKernel;
  readonly events = new Emitter<EngineEvents>();

  private readonly version: string;
  private readonly services = new Map<AnyEngineModule, unknown>();
  private readonly configured = new Map<AnyEngineModule, unknown>();
  private initialized: AnyEngineModule[] = [];

  constructor(options: EngineOptions) {
    const scheduler = new ChangeScheduler();
    const log = new Log({
      clock: options.clock,
      scheduler,
      ...(options.logCapacity !== undefined && { capacity: options.logCapacity }),
    });
    this.kernel = { clock: options.clock, scheduler, log };
    this.version = options.version;
  }

  get log(): Log {
    return this.kernel.log;
  }

  /** Sets a module's options before boot. Overrides the module's defaults. */
  configure<M extends AnyEngineModule>(module: M, options: OptionsOf<M>): void {
    this.configured.set(module, options);
  }

  /** Initializes `modules` (and their transitive dependencies) in dependency order. */
  async boot(modules: readonly AnyEngineModule[]): Promise<void> {
    const startMs = this.kernel.clock.now();
    this.log.display("Init", `kn.engine ${this.version} · editor build`);

    const order = sortModules(modules);
    const names = order.map((m) => m.name);
    this.events.emit("boot:start", { modules: names });
    this.log.display("Modules", names.join(", "));

    for (const module of order) {
      await this.initModule(module);
    }

    this.events.emit("boot:done", {
      ms: this.kernel.clock.now() - startMs,
      modules: this.initialized.map((m) => m.name),
    });
  }

  /** The services of an initialized module. Throws if it isn't initialized. */
  get<M extends AnyEngineModule>(module: M): ServicesOf<M> {
    if (!this.services.has(module)) {
      throw new Error(`Module '${module.name}' is not initialized`);
    }
    return this.services.get(module) as ServicesOf<M>;
  }

  has(module: AnyEngineModule): boolean {
    return this.services.has(module);
  }

  /** Shuts modules down in reverse initialization order. */
  async shutdown(): Promise<void> {
    for (const module of [...this.initialized].reverse()) {
      await module.shutdown?.(this.services.get(module));
    }
    this.initialized = [];
    this.services.clear();
  }

  private async initModule(module: AnyEngineModule): Promise<void> {
    const name = module.name;
    const startMs = this.kernel.clock.now();
    this.events.emit("module:start", { name });

    try {
      const deps = this.resolveDeps(module);
      const options: unknown = this.configured.has(module)
        ? this.configured.get(module)
        : module.options?.();
      const services: unknown = await module.init({ deps, options, kernel: this.kernel });

      this.services.set(module, services);
      this.initialized.push(module);

      const ms = this.kernel.clock.now() - startMs;
      this.log.verbose("Modules", `${name} initialized in ${ms.toFixed(1)} ms`);
      this.events.emit("module:done", { name, ms });
    } catch (thrown) {
      this.handleInitFailure(module, toError(thrown));
    }
  }

  /** Builds `ctx.deps`, failing if a dependency was skipped (an optional module that failed). */
  private resolveDeps(module: AnyEngineModule): Record<string, unknown> {
    const deps: Record<string, unknown> = {};
    for (const dependency of module.dependsOn as readonly AnyEngineModule[]) {
      if (!this.services.has(dependency)) {
        throw new Error(`dependency '${dependency.name}' is unavailable`);
      }
      deps[dependency.name] = this.services.get(dependency);
    }
    return deps;
  }

  private handleInitFailure(module: AnyEngineModule, error: Error): void {
    const optional = module.optional === true;
    this.events.emit("module:error", { name: module.name, error, optional });

    if (optional) {
      this.log.warning(
        module.name,
        `failed to initialize, continuing without it: ${error.message}`,
      );
      return;
    }

    this.log.error(module.name, `failed to initialize: ${error.message}`);
    throw new Error(`${module.name} failed to initialize: ${error.message}`, { cause: error });
  }
}

function toError(thrown: unknown): Error {
  return thrown instanceof Error ? thrown : new Error(String(thrown));
}
